import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Printer, Receipt, Users, CreditCard, Undo2, Eye, Pencil, FileText, Download, BookOpen, BarChart3, ChevronDown, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';
import { PageHeader, Button, Card, Input, Select, Modal, Badge, Th, Td, EmptyState } from '../components/ui';
import { fmtMoney, fmtNum, fmtDate, canWrite, canDeleteBill, statusColor, SIZES } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import InvoicePrint from '../components/InvoicePrint';
import ReceiptPrint from '../components/ReceiptPrint';
import LedgerPrint from '../components/LedgerPrint';
import SalesmanSummaryPrint from '../components/SalesmanSummaryPrint';
import PaymentsLedger from '../components/PaymentsLedger';
import { downloadElementAsPdf, pdfFilename } from '../lib/pdf';


export default function Sales() {
  const { user } = useAuth();
  const write = canWrite(user.role);
  const [tab, setTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [builder, setBuilder] = useState(null); // 'wholesale' | 'retail'
  const [view, setView] = useState(null);
  const [pay, setPay] = useState(null);
  const [ret, setRet] = useState(null);
  const [rcpt, setRcpt] = useState(null);
  const [editInv, setEditInv] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [custModal, setCustModal] = useState(null);

  const load = () => {
    api.get('/invoices').then((r) => setInvoices(r.data));
    api.get('/customers').then((r) => setCustomers(r.data));
    api.get('/products').then((r) => setProducts(r.data));
    api.get('/settings').then((r) => setSettings(r.data));
  };
  useEffect(() => { load(); }, []);

  const delCust = async (id) => { if (!window.confirm('Delete customer?')) return; await api.delete(`/customers/${id}`); toast.success('Deleted'); load(); };

  const openLedger = async (c) => {
    try {
      const r = await api.get(`/customers/${c.id}/ledger`);
      setLedger(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delInv = async (inv) => {
    if (!window.confirm(`Delete bill ${inv.invoiceNumber}? Stock will be restored.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success('Bill deleted · stock restored'); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Sales & Billing" subtitle="Wholesale invoices, retail bills & customers"
        actions={write && tab === 'invoices' && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setBuilder('retail')} data-testid="new-retail-btn"><Plus size={16} /> Retail Bill</Button>
            <Button onClick={() => setBuilder('wholesale')} data-testid="new-wholesale-btn"><Plus size={16} /> Wholesale Invoice</Button>
          </div>
        )} />

      <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-md w-fit">
        {[['invoices', 'Invoices', Receipt], ['customers', 'Customers', Users], ['payments', 'Payments', Wallet], ['dashboard', 'Dashboard', BarChart3]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sales-tab-${k}`}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded transition-colors ${tab === k ? 'bg-white text-[#1E3A8A] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <SalesDashboard />}
      {tab === 'payments' && <PaymentsLedger />}
      {tab === 'invoices' && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr><Th>Invoice #</Th><Th>Date</Th><Th>Customer</Th><Th>Type</Th><Th right>Total</Th><Th right>Paid</Th><Th right>Due</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-slate-50/60 transition-colors" data-testid="invoice-row">
                    <Td><span className="font-mono text-xs font-semibold">{inv.invoiceNumber}</span></Td>
                    <Td>{fmtDate(inv.date)}</Td>
                    <Td className="font-medium text-slate-800">{inv.customerName || 'Walk-in'}</Td>
                    <Td><Badge className={inv.type === 'wholesale' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-cyan-50 text-cyan-700 border-cyan-200'}>{inv.type}</Badge></Td>
                    <Td right>{fmtMoney(inv.total)}</Td>
                    <Td right className="text-emerald-600">{fmtMoney(inv.amountPaid)}</Td>
                    <Td right className={inv.dueAmount > 0 ? 'text-rose-600 font-semibold' : ''}>{fmtMoney(inv.dueAmount)}</Td>
                    <Td><Badge className={statusColor(inv.status)}>{inv.status}</Badge></Td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => setView(inv)} className="text-slate-400 hover:text-[#1E3A8A] p-1" title="View / Print" data-testid="view-invoice-btn"><Eye size={15} /></button>
                      {write && inv.dueAmount > 0 && <button onClick={() => setPay(inv)} className="text-slate-400 hover:text-emerald-600 p-1" title="Record payment" data-testid="pay-invoice-btn"><CreditCard size={15} /></button>}
                      {inv.amountPaid > 0 && <button onClick={() => setRcpt(inv)} className="text-slate-400 hover:text-[#B45309] p-1" title="Receipt voucher" data-testid="receipt-invoice-btn"><FileText size={15} /></button>}
                      {write && <button onClick={() => setRet(inv)} className="text-slate-400 hover:text-amber-600 p-1" title="Return" data-testid="return-invoice-btn"><Undo2 size={15} /></button>}
                      {write && <button onClick={() => setEditInv(inv)} className="text-slate-400 hover:text-blue-600 p-1" title="Edit bill" data-testid="edit-invoice-btn"><Pencil size={15} /></button>}
                      {canDeleteBill(user.role) && <button onClick={() => delInv(inv)} className="text-slate-400 hover:text-rose-600 p-1" title="Delete bill" data-testid="delete-invoice-btn"><Trash2 size={15} /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!invoices.length && <EmptyState title="No invoices yet" icon={Receipt} />}
        </Card>
      )}
      {tab === 'customers' && (
        <div>
          {write && <div className="mb-4"><Button onClick={() => setCustModal('new')} data-testid="add-customer-btn"><Plus size={16} /> Customer</Button></div>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customers.map((c) => (
              <Card key={c.id} className="p-4" data-testid="customer-card">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-heading font-bold text-slate-900">{c.companyName}</p>
                    <p className="text-xs text-slate-400">{c.contactPerson} · {c.phone}</p>
                  </div>
                  {write && <div className="flex">
                    <button onClick={() => setCustModal(c)} className="text-slate-400 hover:text-blue-600 p-1"><Pencil size={14} /></button>
                    <button onClick={() => delCust(c.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 size={14} /></button>
                  </div>}
                </div>
                <p className="text-xs text-slate-500 mt-2">{c.address}</p>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-slate-400">Outstanding</span>
                  <span className={`num font-semibold ${c.outstandingBalance > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fmtMoney(c.outstandingBalance)}</span>
                </div>
                {write && (
                  <Button size="sm" variant="outline" className="w-full mt-3" onClick={() => openLedger(c)} data-testid="customer-ledger-btn">
                    <BookOpen size={13} /> Ledger
                  </Button>
                )}
              </Card>
            ))}
            {!customers.length && <EmptyState title="No customers" icon={Users} />}
          </div>
        </div>
      )}

      {builder && <InvoiceBuilder type={builder} products={products} customers={customers} onClose={() => setBuilder(null)} onSaved={() => { setBuilder(null); load(); }} />}
      {view && <InvoiceView invoice={view} company={settings?.company} onClose={() => setView(null)} />}
      {rcpt && <ReceiptView invoice={rcpt} company={settings?.company} onClose={() => setRcpt(null)} />}
      {pay && <PaymentModal invoice={pay} onClose={() => setPay(null)} onSaved={() => { setPay(null); load(); }} />}
      {ret && <ReturnModal invoice={ret} onClose={() => setRet(null)} onSaved={() => { setRet(null); load(); }} />}
      {editInv && <EditInvoiceModal invoice={editInv} products={products} onClose={() => setEditInv(null)} onSaved={() => { setEditInv(null); load(); }} />}
      {ledger && <LedgerModal ledger={ledger} company={settings?.company} onClose={() => setLedger(null)} />}
      {custModal && <CustomerModal customer={custModal === 'new' ? null : custModal} onClose={() => setCustModal(null)} onSaved={() => { setCustModal(null); load(); }} />}
    </div>
  );
}

function InvoiceBuilder({ type, products, customers, onClose, onSaved }) {
  const isWholesale = type === 'wholesale';
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [items, setItems] = useState([{ productId: '', productName: '', size: 'Universal', quantity: 1, price: 0 }]);
  const [discount, setDiscount] = useState(0);
  const [shippingCharges, setShipping] = useState(0);
  const [amountPaid, setAmountPaid] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('Bank Transfer');
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);

  const setItem = (i, patch) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const pickProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    if (p) setItem(i, { productId: pid, productName: p.name, price: isWholesale ? p.wholesalePrice : p.retailPrice });
    else setItem(i, { productId: '', productName: '' });
  };
  const addRow = () => setItems((p) => [...p, { productId: '', productName: '', size: 'Universal', quantity: 1, price: 0 }]);
  const removeRow = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const subtotal = useMemo(() => items.reduce((s, it) => s + (Number(it.quantity) * Number(it.price)), 0), [items]);
  const total = Math.max(0, subtotal - Number(discount) + Number(shippingCharges));

  const onCustomer = (id) => {
    setCustomerId(id);
    const c = customers.find((x) => x.id === id);
    if (c) setCustomerName(c.companyName);
  };

  const save = async () => {
    const cleanItems = items.filter((it) => it.productName && Number(it.quantity) > 0)
      .map((it) => ({ ...it, quantity: Number(it.quantity), price: Number(it.price) }));
    if (!cleanItems.length) { toast.error('Add at least one line item'); return; }
    try {
      if (isWholesale) {
        await api.post('/invoices/wholesale', {
          customerId: customerId || null, customerName, date,
          items: cleanItems, discount: Number(discount), shippingCharges: Number(shippingCharges),
          amountPaid: Number(amountPaid), paymentMethod,
        });
      } else {
        await api.post('/invoices/retail', {
          customerName, customerMobile, date,
          items: cleanItems, discount: Number(discount), shippingCharges: Number(shippingCharges),
        });
      }
      toast.success('Invoice created'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Modal open size="lg" title={isWholesale ? 'New Wholesale Invoice' : 'New Retail Bill'} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-invoice-btn">Create Invoice</Button></>}>
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Input label="Invoice Date" type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} data-testid="invoice-date" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          {isWholesale ? (
            <>
              <Select label="Customer" value={customerId} onChange={(e) => onCustomer(e.target.value)} data-testid="invoice-customer">
                <option value="">— Select customer —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
              </Select>
              <Input label="Customer Name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
            </>
          ) : (
            <>
              <Input label="Customer Name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Walk-in customer" data-testid="retail-customer-name" />
              <Input label="Mobile" value={customerMobile} onChange={(e) => setCustomerMobile(e.target.value)} />
            </>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-600 uppercase">Line Items</span>
            <Button size="sm" variant="outline" onClick={addRow} data-testid="add-line-btn"><Plus size={13} /> Add line</Button>
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid="line-item">
                <div className="col-span-4">
                  <Select value={it.productId} onChange={(e) => pickProduct(i, e.target.value)}>
                    <option value="">Custom / select…</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Select>
                </div>
                <div className="col-span-2"><Input placeholder="Item name" value={it.productName} onChange={(e) => setItem(i, { productName: e.target.value })} /></div>
                <div className="col-span-1">
                  <Select value={it.size} onChange={(e) => setItem(i, { size: e.target.value })}>
                    {SIZES.map((s) => <option key={s}>{s}</option>)}
                  </Select>
                </div>
                <div className="col-span-2"><Input type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} /></div>
                <div className="col-span-2"><Input type="number" step="0.001" placeholder="Price" value={it.price} onChange={(e) => setItem(i, { price: e.target.value })} /></div>
                <div className="col-span-1 pb-2 text-right">
                  <button onClick={() => removeRow(i)} className="text-slate-400 hover:text-rose-600"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-3">
            <Input label="Discount (KWD)" type="number" step="0.001" value={discount} onChange={(e) => setDiscount(e.target.value)} />
            <Input label="Delivery / Shipping (KWD)" type="number" step="0.001" value={shippingCharges} onChange={(e) => setShipping(e.target.value)} />
            {isWholesale && <>
              <Input label="Amount Paid Now (KWD)" type="number" step="0.001" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} data-testid="invoice-paid" />
              <Select label="Payment Method" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <option>Bank Transfer</option><option>Cash</option><option>Cheque</option>
              </Select>
            </>}
          </div>
          <div className="bg-slate-50 rounded-md p-4 border border-slate-200 self-start">
            <Row label="Subtotal" value={subtotal} />
            <Row label="Discount" value={-Number(discount)} />
            <Row label="Delivery" value={Number(shippingCharges)} />
            <div className="border-t border-slate-200 my-2" />
            <Row label="Total" value={total} bold />
            {isWholesale && <Row label="Due" value={Math.max(0, total - Number(amountPaid))} accent />}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Row({ label, value, bold, accent }) {
  return (
    <div className="flex justify-between text-sm py-0.5">
      <span className={`${bold ? 'font-semibold text-slate-800' : 'text-slate-500'}`}>{label}</span>
      <span className={`num ${bold ? 'font-bold text-slate-900' : ''} ${accent ? 'text-rose-600 font-semibold' : ''}`}>{fmtMoney(value)}</span>
    </div>
  );
}

function InvoiceView({ invoice, company, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      await downloadElementAsPdf(document.getElementById('print-area'), pdfFilename(invoice));
    } catch { toast.error('Could not generate PDF'); }
    setDownloading(false);
  };
  return (
    <Modal open size="lg" title={`Invoice ${invoice.invoiceNumber}`} onClose={onClose}
      footer={<>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="print-invoice-btn"><Printer size={16} /> Print</Button>
        <Button onClick={download} disabled={downloading} data-testid="download-invoice-pdf-btn"><Download size={16} /> {downloading ? 'Generating…' : 'Download PDF'}</Button>
      </>}>
      <div id="print-area">
        <InvoicePrint invoice={invoice} company={company} />
      </div>
    </Modal>
  );
}

function ReceiptView({ invoice, company, onClose }) {
  const lastPayment = invoice.paymentHistory?.length ? invoice.paymentHistory[invoice.paymentHistory.length - 1] : null;
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      await downloadElementAsPdf(document.getElementById('print-area'), pdfFilename(invoice, 'RV-'), 'landscape');
    } catch { toast.error('Could not generate PDF'); }
    setDownloading(false);
  };
  return (
    <Modal open size="lg" title={`Receipt Voucher · ${invoice.invoiceNumber}`} onClose={onClose}
      footer={<>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="print-receipt-btn"><Printer size={16} /> Print</Button>
        <Button onClick={download} disabled={downloading} data-testid="download-receipt-pdf-btn"><Download size={16} /> {downloading ? 'Generating…' : 'Download PDF'}</Button>
      </>}>
      <div id="print-area">
        <ReceiptPrint invoice={invoice} payment={lastPayment} company={company} />
      </div>
    </Modal>
  );
}

function LedgerModal({ ledger, company, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const name = (ledger.customer.companyName || 'Customer').replace(/[^\w\u0600-\u06FF\s-]/g, '').trim().replace(/\s+/g, '-');
      await downloadElementAsPdf(document.getElementById('print-area'), `${name}-Ledger.pdf`);
    } catch { toast.error('Could not generate PDF'); }
    setDownloading(false);
  };
  return (
    <Modal open size="lg" title={`Ledger · ${ledger.customer.companyName}`} onClose={onClose}
      footer={<>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="print-ledger-btn"><Printer size={16} /> Print</Button>
        <Button onClick={download} disabled={downloading} data-testid="download-ledger-pdf-btn"><Download size={16} /> {downloading ? 'Generating…' : 'Download PDF'}</Button>
      </>}>
      <div id="print-area">
        <LedgerPrint ledger={ledger} company={company} />
      </div>
    </Modal>
  );
}

function EditInvoiceModal({ invoice, products, onClose, onSaved }) {
  const [f, setF] = useState({
    customerName: invoice.customerName || '',
    customerMobile: invoice.customerMobile || '',
    date: invoice.date || '',
    discount: invoice.discount || 0,
    shippingCharges: invoice.shippingCharges || 0,
  });
  const [items, setItems] = useState(invoice.items.map((it) => ({ ...it })));
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setItem = (i, patch) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const pickProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    if (p) setItem(i, { productId: pid, productName: p.name, productNameAr: p.nameAr || '', price: p.sellingPrice ?? p.retailPrice ?? 0 });
  };
  const subtotal = items.reduce((s, it) => s + Number(it.quantity) * Number(it.price), 0);
  const save = async () => {
    const clean = items.filter((it) => it.productName && Number(it.quantity) > 0)
      .map((it) => ({ productId: it.productId || '', productName: it.productName, productNameAr: it.productNameAr || '', size: it.size || 'Universal', quantity: Number(it.quantity), price: Number(it.price) }));
    if (!clean.length) { toast.error('Bill must have at least one item'); return; }
    try {
      await api.put(`/invoices/${invoice.id}`, {
        ...f, discount: Number(f.discount), shippingCharges: Number(f.shippingCharges), items: clean,
      });
      toast.success('Bill updated · stock adjusted'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const newTotal = Math.max(0, subtotal - Number(f.discount || 0) + Number(f.shippingCharges || 0));
  return (
    <Modal open size="lg" title={`Edit Bill · ${invoice.invoiceNumber}`} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-edit-invoice-btn">Save Changes</Button></>}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input label="Customer Name" value={f.customerName} onChange={(e) => set('customerName', e.target.value)} data-testid="edit-inv-customer" />
          <Input label="Date" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} />
        </div>
        {invoice.type === 'retail' && <Input label="Customer Mobile" value={f.customerMobile} onChange={(e) => set('customerMobile', e.target.value)} />}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-600 uppercase">Items</span>
            <Button size="sm" variant="outline" onClick={() => setItems((p) => [...p, { productId: '', productName: '', size: 'Universal', quantity: 1, price: 0 }])} data-testid="edit-inv-add-item-btn"><Plus size={13} /> Add Item</Button>
          </div>
          <div className="grid grid-cols-12 gap-2 mb-1 text-[10px] font-semibold text-slate-400 uppercase">
            <div className="col-span-4">Product</div><div className="col-span-2">Size</div><div className="col-span-2">Qty</div><div className="col-span-2">Price</div><div className="col-span-1 text-right">Total</div><div className="col-span-1" />
          </div>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid="edit-inv-item-row">
              <div className="col-span-4">
                <Select value={it.productId || ''} onChange={(e) => pickProduct(i, e.target.value)}>
                  <option value="">{it.productName || 'Select product…'}</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
              </div>
              <div className="col-span-2">
                <Select value={it.size || 'Universal'} onChange={(e) => setItem(i, { size: e.target.value })}>
                  {SIZES.map((s) => <option key={s}>{s}</option>)}
                </Select>
              </div>
              <div className="col-span-2"><Input type="number" step="1" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} data-testid="edit-inv-item-qty" /></div>
              <div className="col-span-2"><Input type="number" step="0.001" value={it.price} onChange={(e) => setItem(i, { price: e.target.value })} data-testid="edit-inv-item-price" /></div>
              <div className="col-span-1 text-right num text-sm font-semibold">{fmtNum(Number(it.quantity) * Number(it.price))}</div>
              <div className="col-span-1 text-right"><button onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-600"><Trash2 size={15} /></button></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Discount (KWD)" type="number" step="0.001" value={f.discount} onChange={(e) => set('discount', e.target.value)} data-testid="edit-inv-discount" />
          <Input label="Delivery (KWD)" type="number" step="0.001" value={f.shippingCharges} onChange={(e) => set('shippingCharges', e.target.value)} />
        </div>
        <p className="text-xs text-slate-500">Subtotal <span className="num">{fmtMoney(subtotal)}</span> · New total <span className="font-semibold text-slate-800 num">{fmtMoney(newTotal)}</span>. Product stock adjusts automatically to match the edited items.</p>
      </div>
    </Modal>
  );
}

function PaymentModal({ invoice, onClose, onSaved }) {
  const [amount, setAmount] = useState(invoice.dueAmount);
  const [method, setMethod] = useState('Cash');
  const [reference, setReference] = useState('');
  const save = async () => {
    try {
      await api.post(`/invoices/${invoice.id}/payment`, { amount: Number(amount), method, reference });
      toast.success('Payment recorded'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="sm" title={`Record Payment · ${invoice.invoiceNumber}`} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="success" onClick={save} data-testid="save-payment-btn">Record</Button></>}>
      <p className="text-sm text-slate-500 mb-4">Amount due: <span className="font-semibold num text-rose-600">{fmtMoney(invoice.dueAmount)}</span></p>
      <div className="space-y-4">
        <Input label="Amount (KWD)" type="number" step="0.001" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-input" />
        <Select label="Method" value={method} onChange={(e) => setMethod(e.target.value)}>
          <option>Cash</option><option>Bank Transfer</option><option>Cheque</option>
        </Select>
        <Input label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} />
      </div>
    </Modal>
  );
}

function ReturnModal({ invoice, onClose, onSaved }) {
  const [rows, setRows] = useState(invoice.items.filter((i) => i.productId).map((i) => ({ productId: i.productId, size: i.size || 'Universal', name: i.productName, max: i.quantity, quantity: 0 })));
  const [refundAmount, setRefund] = useState(0);
  const [notes, setNotes] = useState('');
  const setRow = (i, q) => setRows((p) => p.map((r, idx) => idx === i ? { ...r, quantity: q } : r));
  const save = async () => {
    const items = rows.filter((r) => Number(r.quantity) > 0).map((r) => ({ productId: r.productId, size: r.size, quantity: Number(r.quantity) }));
    if (!items.length) { toast.error('Enter return quantity'); return; }
    try {
      await api.post(`/invoices/${invoice.id}/return`, { items, refundAmount: Number(refundAmount), notes });
      toast.success('Return recorded'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="md" title={`Sales Return · ${invoice.invoiceNumber}`} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="accent" onClick={save} data-testid="save-return-btn">Process Return</Button></>}>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="flex-1 text-sm text-slate-700">{r.name} <span className="text-slate-400">· {r.size} (max {r.max})</span></span>
            <Input type="number" max={r.max} value={r.quantity} onChange={(e) => setRow(i, e.target.value)} className="w-24" />
          </div>
        ))}
        <Input label="Refund Amount (KWD)" type="number" step="0.001" value={refundAmount} onChange={(e) => setRefund(e.target.value)} />
        <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
    </Modal>
  );
}

function CustomerModal({ customer, onClose, onSaved }) {
  const [f, setF] = useState(customer || { companyName: '', contactPerson: '', phone: '', email: '', address: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      if (customer) await api.put(`/customers/${customer.id}`, f);
      else await api.post('/customers', f);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open size="sm" title={customer ? 'Edit Customer' : 'New Customer'} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-customer-btn">Save</Button></>}>
      <div className="space-y-4">
        <Input label="Company Name" value={f.companyName} onChange={(e) => set('companyName', e.target.value)} data-testid="customer-company" />
        <Input label="Contact Person" value={f.contactPerson} onChange={(e) => set('contactPerson', e.target.value)} />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
          <Input label="Email" value={f.email} onChange={(e) => set('email', e.target.value)} />
        </div>
        <Input label="Address" value={f.address} onChange={(e) => set('address', e.target.value)} />
      </div>
    </Modal>
  );
}

function SalesDashboard() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = `${today.slice(0, 7)}-01`;
  const [range, setRange] = useState({ from: monthStart, to: today });
  const [mode, setMode] = useState('month');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [summary, setSummary] = useState(null);

  const fetchData = (from, to) => {
    const params = [];
    if (from) params.push(`from_date=${from}`);
    if (to) params.push(`to_date=${to}`);
    api.get(`/dashboard/sales${params.length ? `?${params.join('&')}` : ''}`).then((r) => setData(r.data));
  };
  useEffect(() => { fetchData(monthStart, today); }, []);

  const quick = (m) => {
    setMode(m);
    if (m === 'month') { setRange({ from: monthStart, to: today }); fetchData(monthStart, today); }
    if (m === 'today') { setRange({ from: today, to: today }); fetchData(today, today); }
    if (m === 'lifetime') { setRange({ from: '', to: '' }); fetchData(null, null); }
  };

  return (
    <div>
      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          {[['today', 'Today'], ['month', 'This Month'], ['lifetime', 'Lifetime']].map(([k, label]) => (
            <Button key={k} size="sm" variant={mode === k ? 'primary' : 'outline'} onClick={() => quick(k)} data-testid={`salesdash-${k}-btn`}>{label}</Button>
          ))}
          <Input label="From" type="date" value={range.from} onChange={(e) => setRange((p) => ({ ...p, from: e.target.value }))} data-testid="salesdash-from" />
          <Input label="To" type="date" value={range.to} onChange={(e) => setRange((p) => ({ ...p, to: e.target.value }))} data-testid="salesdash-to" />
          <Button size="sm" onClick={() => { setMode('custom'); fetchData(range.from, range.to); }} data-testid="salesdash-apply-btn">Apply</Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-heading font-bold text-slate-700">Salesman-wise Sales {mode === 'lifetime' ? '(Lifetime)' : `(${range.from || '…'} → ${range.to || '…'})`}</div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Salesman</Th><Th right>Bills</Th><Th right>Sales (Period)</Th><Th right>Lifetime Bills</Th><Th right>Lifetime Sales</Th><Th className="text-right">Product-wise</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.rows || []).map((r) => (
                <React.Fragment key={r.salesman}>
                  <tr data-testid="salesdash-row" className="hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpen(open === r.salesman ? null : r.salesman)}>
                    <Td className="font-medium text-slate-800">{r.salesman}</Td>
                    <Td right className="num">{fmtNum(r.count)}</Td>
                    <Td right className="num font-semibold">{fmtMoney(r.amount)}</Td>
                    <Td right className="num">{fmtNum(r.lifetimeCount)}</Td>
                    <Td right className="num">{fmtMoney(r.lifetimeAmount)}</Td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <button onClick={(e) => { e.stopPropagation(); setSummary(r.salesman); }} className="text-slate-400 hover:text-[#1E3A8A] p-1 mr-1" title="Date-wise summary & PDF" data-testid="salesman-summary-btn"><FileText size={15} /></button>
                      <ChevronDown size={15} className={`inline text-slate-400 transition-transform ${open === r.salesman ? 'rotate-180' : ''}`} />
                    </td>
                  </tr>
                  {open === r.salesman && (
                    <tr data-testid="salesdash-products-row">
                      <td colSpan="6" className="bg-slate-50/60 px-6 py-3">
                        {r.products.length ? (
                          <table className="w-full max-w-xl">
                            <thead><tr><Th>Product</Th><Th right>Qty</Th><Th right>Amount</Th></tr></thead>
                            <tbody className="divide-y divide-slate-100">
                              {r.products.map((p) => (
                                <tr key={p.productName}>
                                  <Td className="text-slate-700">{p.productName}</Td>
                                  <Td right className="num">{fmtNum(p.qty)}</Td>
                                  <Td right className="num font-semibold">{fmtMoney(p.amount)}</Td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : <p className="text-xs text-slate-400">No sales in this period.</p>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {!data?.rows?.length && <EmptyState title="No sales data" icon={BarChart3} />}
      </Card>
      {summary && <SalesmanSummaryModal salesman={summary} onClose={() => setSummary(null)} />}
    </div>
  );
}

function SalesmanSummaryModal({ salesman, onClose }) {
  const [month, setMonth] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [company, setCompany] = useState(null);
  const [downloading, setDownloading] = useState(false);
  useEffect(() => {
    api.get('/invoices').then((r) => setInvoices(r.data.filter((i) => (i.createdBy || 'Admin') === salesman)));
    api.get('/settings').then((r) => setCompany(r.data?.company)).catch(() => {});
  }, [salesman]);

  const filtered = month ? invoices.filter((i) => (i.date || '').startsWith(month)) : invoices;
  const map = {};
  for (const inv of filtered) {
    const d = inv.date || '—';
    if (!map[d]) map[d] = { date: d, bills: 0, qty: 0, amount: 0, products: {} };
    map[d].bills += 1;
    map[d].amount += Number(inv.total || 0);
    for (const it of inv.items || []) {
      map[d].qty += Number(it.quantity || 0);
      map[d].products[it.productName] = (map[d].products[it.productName] || 0) + Number(it.quantity || 0);
    }
  }
  const rows = Object.values(map).sort((a, b) => (a.date < b.date ? 1 : -1));
  const totals = rows.reduce((a, r) => ({ bills: a.bills + r.bills, qty: a.qty + r.qty, amount: a.amount + r.amount }), { bills: 0, qty: 0, amount: 0 });
  const periodLabel = month ? new Date(`${month}-01`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : 'All Time';

  const download = async () => {
    setDownloading(true);
    try {
      const name = salesman.replace(/[^\w\u0600-\u06FF\s-]/g, '').trim().replace(/\s+/g, '-');
      await downloadElementAsPdf(document.getElementById('salesman-summary-print'), `${name}-Sales-${month || 'All'}.pdf`);
    } catch { toast.error('Could not generate PDF'); }
    setDownloading(false);
  };

  return (
    <Modal open title={`${salesman} · Date-wise Sales`} onClose={onClose} size="lg"
      footer={<>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={download} disabled={downloading} data-testid="download-sales-summary-pdf-btn"><Download size={16} /> {downloading ? 'Generating…' : 'Download PDF'}</Button>
      </>}>
      <div className="flex items-end gap-3 mb-4">
        <Input label="Month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="sales-summary-month-input" />
        {month && <Button size="sm" variant="outline" onClick={() => setMonth('')} data-testid="sales-summary-alltime-btn">All Time</Button>}
        <span className="text-xs text-slate-400 pb-2.5">{periodLabel}</span>
      </div>
      <div className="overflow-x-auto max-h-[55vh] overflow-y-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr><Th>Date</Th><Th>Products</Th><Th right>Bills</Th><Th right>Qty</Th><Th right>Sales</Th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.date} data-testid="sales-summary-row" className="hover:bg-slate-50/60">
                <Td className="font-medium text-slate-800">{fmtDate(r.date)}</Td>
                <Td className="text-xs text-slate-500">{Object.entries(r.products).map(([n, q]) => `${n} (${q})`).join(', ')}</Td>
                <Td right className="num">{fmtNum(r.bills)}</Td>
                <Td right className="num">{fmtNum(r.qty)}</Td>
                <Td right className="num font-semibold">{fmtMoney(r.amount)}</Td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-slate-50 border-t border-slate-200">
              <tr>
                <Td className="font-bold text-slate-800">Total</Td><Td />
                <Td right className="num font-bold">{fmtNum(totals.bills)}</Td>
                <Td right className="num font-bold">{fmtNum(totals.qty)}</Td>
                <Td right className="num font-bold">{fmtMoney(totals.amount)}</Td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {!rows.length && <EmptyState title="No sales in this period" icon={BarChart3} />}
      <div style={{ position: 'fixed', left: '-20000px', top: 0 }}>
        <div id="salesman-summary-print">
          <SalesmanSummaryPrint salesmanName={salesman} rows={rows} totals={totals} periodLabel={periodLabel} company={company} />
        </div>
      </div>
    </Modal>
  );
}
